// Cross-page state for the build flow. The Configure wizard mutates the
// draft RunConfig here; Landing seeds it when the user picks a mode.
// Persisted to sessionStorage so a hard refresh during Configure doesn't
// wipe the user's selections.

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Mode, RunConfig } from "@/lib/types";

const DEFAULT_CONFIG: RunConfig = {
  mode: "full",
  profileId: "cirrus_renewable_energy",
  volume: "medium",
  salesCloud: true,
  serviceCloud: true,
  stories: [],
  // GA defaults (2026-05-12). C360 SDM + Insights Apps are both
  // unblocked — the ATF cascade fail-fast + damaged-org preflight in
  // ``app/services/atf_deployer.py`` guard any edge-case install
  // failure. Lift any of these defaults off ``true`` only if you have
  // a concrete reason; the proven path runs the full stack.
  pipeline: {
    crm: true,
    tdsx: false,
    dc: true,
    sdm: true,
    apps: true,
    tabnext: true,
    refresh: false,
  },
  mergeMode: "smart",
  boostData: true,
  // PR 1 Week 3 (2026-06-01): default Tableau outputs. Only the
  // ``tdsx`` step is on so legacy hyper-mode runs continue to
  // produce a downloadable .tdsx without any TWBX/Cloud round-
  // trips. The wizard reveals the full panel when the user
  // toggles the TDSX pipeline layer in Stage 4.
  tableauOutputs: {
    tdsx: true,
    twbx: false,
    publish: false,
    pulse: false,
    patProfileId: null,
    patOneOff: null,
  },
};

// PIPELINE_BY_MODE for each preset Mode. The ``full`` / ``continue``
// presets now enable the C360 SDM tile (its ATF blockers were
// resolved on 2026-05-12 — see ``docs/GA_READINESS_HANDOFF.md``).
const PIPELINE_BY_MODE: Record<Mode, Partial<RunConfig["pipeline"]>> = {
  crm_only:  { crm: true,  tdsx: false, dc: false, sdm: false, apps: false, tabnext: false },
  tdsx_only: { crm: false, tdsx: true,  dc: false, sdm: false, apps: false, tabnext: false },
  full:      { crm: true,  tdsx: false, dc: true,  sdm: true,  apps: true,  tabnext: true },
  continue:  { crm: false, tdsx: false, dc: true,  sdm: true,  apps: true,  tabnext: true },
  refresh:   { refresh: true },
  custom:    {},
};

interface ConfigState {
  config: RunConfig;
  setConfig: (mut: Partial<RunConfig> | ((c: RunConfig) => RunConfig)) => void;
  pickMode: (mode: Mode, continueRunId?: string) => void;
  reset: () => void;
}

export const useConfigStore = create<ConfigState>()(
  persist(
    (set) => ({
      config: { ...DEFAULT_CONFIG },
      setConfig: (mut) =>
        set((state) => ({
          config:
            typeof mut === "function"
              ? mut(state.config)
              : { ...state.config, ...mut },
        })),
      pickMode: (mode, continueRunId) =>
        set((state) => ({
          config: {
            ...state.config,
            mode,
            continueRunId,
            pipeline: { ...state.config.pipeline, ...PIPELINE_BY_MODE[mode] },
          },
        })),
      reset: () => set({ config: { ...DEFAULT_CONFIG } }),
    }),
    {
      name: "qb-v2-config",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
